import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import FacilitatorProgramAssignment from '../components/FacilitatorProgramAssignment';
import { DOCUMENT_CATEGORIES } from '../config/documentCategories';
import qaService from '../services/qaService';
import { downloadProgramDocuments } from '../services/programDocumentDownloadService';
import '../styles/brand.css';
import { UI_TIMEOUTS } from '../constants/ui';

const ProgramManagement = () => {
  const { currentUser, userProfile } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [modules, setModules] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [downloadingProgramId, setDownloadingProgramId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'technical',
    duration: '',
    level: 'beginner',
    prerequisites: '',
    objectives: '',
    status: 'active',
    capacity: 100,
    requiredDocuments: []
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load programs
      const programsCollection = collection(db, 'programs');
      const programsSnapshot = await getDocs(programsCollection);
      const programsData = programsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now())
      }));
      
      // Load modules
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Load cohorts
      const cohortsCollection = collection(db, 'cohorts');
      const cohortsSnapshot = await getDocs(cohortsCollection);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPrograms(programsData);
      setModules(modulesData);
      setCohorts(cohortsData);
      
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading program data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (program = null) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        name: program.name,
        description: program.description || '',
        category: program.category || 'technical',
        duration: program.duration || '',
        level: program.level || 'beginner',
        prerequisites: program.prerequisites || '',
        objectives: program.objectives || '',
        status: program.status || 'active',
        capacity: program.capacity || 100,
        requiredDocuments: program.requiredDocuments || []
      });
    } else {
      setEditingProgram(null);
      setFormData({
        name: '',
        description: '',
        category: 'technical',
        duration: '',
        level: 'beginner',
        prerequisites: '',
        objectives: '',
        status: 'active',
        capacity: 100,
        requiredDocuments: []
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProgram(null);
    setValidated(false);
    setSubmitting(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDocumentToggle = (docKey) => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.includes(docKey)
        ? prev.requiredDocuments.filter(d => d !== docKey)
        : [...prev.requiredDocuments, docKey]
    }));
  };

  const handleSelectAllDocuments = () => {
    const allDocKeys = Object.keys(DOCUMENT_CATEGORIES);
    setFormData(prev => ({
      ...prev,
      requiredDocuments: allDocKeys
    }));
  };

  const handleDeselectAllDocuments = () => {
    setFormData(prev => ({
      ...prev,
      requiredDocuments: []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);
    if (!e.target.checkValidity()) {
      e.stopPropagation();
      return;
    }
    setSubmitting(true);
    try {
      const programData = {
        ...formData,
        capacity: parseInt(formData.capacity),
        updatedAt: serverTimestamp()
      };

      if (editingProgram) {
        // Update existing program
        await updateDoc(doc(db, 'programs', editingProgram.id), programData);
        showAlert('Program updated successfully!', 'success');
      } else {
        // Create new program
        await addDoc(collection(db, 'programs'), {
          ...programData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid
        });
        showAlert('Program created successfully!', 'success');
      }
      
      handleCloseModal();
      loadData();
      
    } catch (error) {
      devError('Error saving program:', error);
      showAlert('Error saving program', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (programId) => {
    if (window.confirm('Are you sure you want to delete this program? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'programs', programId));
        showAlert('Program deleted successfully!', 'success');
        loadData();
      } catch (error) {
        devError('Error deleting program:', error);
        showAlert('Error deleting program', 'danger');
      }
    }
  };

  const handleBulkDownloadProgramDocuments = async (program) => {
    if (!program?.id) return;

    try {
      setDownloadingProgramId(program.id);
      showAlert(`Preparing ${program.name} documents for download...`, 'info');

      const grouped = await qaService.getDocumentsGroupedByProgram();
      const programDocuments = grouped?.grouped?.[program.id]?.documents || [];

      if (!programDocuments.length) {
        showAlert(`No active documents found for ${program.name}.`, 'warning');
        return;
      }

      const result = await downloadProgramDocuments({
        programId: program.id,
        programName: program.name
      });
      showAlert(`Prepared ${result.fileCount || programDocuments.length} documents for ${program.name} as a zip.`, 'success');
    } catch (error) {
      devError('Error downloading program documents:', error);
      showAlert(`Failed to download documents for ${program?.name || 'program'}.`, 'danger');
    } finally {
      setDownloadingProgramId('');
    }
  };

  const getModuleCount = (programId) => {
    return modules.filter(module => module.programId === programId).length;
  };

  const getCohortCount = (programId) => {
    return cohorts.filter(cohort => cohort.programId === programId).length;
  };

  const getStatusBadge = (status) => {
    const variants = {
      'active': 'success',
      'inactive': 'secondary',
      'draft': 'warning',
      'archived': 'dark'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getLevelBadge = (level) => {
    const variants = {
      'beginner': 'success',
      'intermediate': 'warning',
      'advanced': 'danger',
      'expert': 'dark'
    };
    return <Badge bg={variants[level] || 'secondary'}>{level}</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access program management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="programs" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading programs...</p>
          </div>
        </Container>
      </>
    );
  }

  const programsByCategory = programs.reduce((acc, program) => {
    const category = program.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(program);
    return acc;
  }, {});

  return (
    <>
      <AdminNavbar currentPage="programs" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Program Management</h1>
              <p className="mission-text mb-4">
                Configure and manage training programs
              </p>
              <p className="mb-0">
                Design, configure, and manage comprehensive training programs with structured curricula, learning objectives, and assessment criteria to deliver effective educational experiences.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light" onClick={() => handleShowModal()}>
                Add New Program
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Programs Overview */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{programs.length}</h3>
                <small className="text-muted">Total Programs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{programs.filter(p => p.status === 'active').length}</h3>
                <small className="text-muted">Active Programs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-info">{modules.length}</h3>
                <small className="text-muted">Total Modules</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{cohorts.length}</h3>
                <small className="text-muted">Active Cohorts</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Program Management Tabs */}
        <Card>
          <Card.Header>
            <h5>Program Management</h5>
          </Card.Header>
          <Card.Body>
            <Tabs defaultActiveKey="programs" className="mb-3">
              <Tab eventKey="programs" title="All Programs">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th>Category</th>
                      <th>Level</th>
                      <th>Duration</th>
                      <th>Modules</th>
                      <th>Cohorts</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programs.map((program) => (
                      <tr key={program.id}>
                        <td>
                          <strong>{program.name}</strong>
                          {program.description && (
                            <div><small className="text-muted">{program.description}</small></div>
                          )}
                        </td>
                        <td>
                          <Badge bg="info">{program.category}</Badge>
                        </td>
                        <td>{getLevelBadge(program.level)}</td>
                        <td>{program.duration}</td>
                        <td>
                          <Badge bg="secondary">{getModuleCount(program.id)}</Badge>
                        </td>
                        <td>
                          <Badge bg="primary">{getCohortCount(program.id)}</Badge>
                        </td>
                        <td>{getStatusBadge(program.status)}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-1"
                            onClick={() => handleShowModal(program)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            disabled={downloadingProgramId === program.id}
                            onClick={() => handleBulkDownloadProgramDocuments(program)}
                          >
                            {downloadingProgramId === program.id ? 'Downloading...' : 'Download Documents'}
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(program.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {programs.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          No programs found. Create your first program to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Tab>
              
              {Object.entries(programsByCategory).map(([category, categoryPrograms]) => (
                <Tab key={category} eventKey={category} title={`${category} (${categoryPrograms.length})`}>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Program</th>
                        <th>Level</th>
                        <th>Duration</th>
                        <th>Modules</th>
                        <th>Cohorts</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryPrograms.map((program) => (
                        <tr key={program.id}>
                          <td>
                            <strong>{program.name}</strong>
                            {program.description && (
                              <div><small className="text-muted">{program.description}</small></div>
                            )}
                          </td>
                          <td>{getLevelBadge(program.level)}</td>
                          <td>{program.duration}</td>
                          <td>
                            <Badge bg="secondary">{getModuleCount(program.id)}</Badge>
                          </td>
                          <td>
                            <Badge bg="primary">{getCohortCount(program.id)}</Badge>
                          </td>
                          <td>{getStatusBadge(program.status)}</td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="me-1"
                              onClick={() => handleShowModal(program)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-1"
                              disabled={downloadingProgramId === program.id}
                              onClick={() => handleBulkDownloadProgramDocuments(program)}
                            >
                              {downloadingProgramId === program.id ? 'Downloading...' : 'Download Documents'}
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => handleDelete(program.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Tab>
              ))}

              {/* Facilitator Assignments Tab */}
              <Tab eventKey="assignments" title="Facilitator Assignments">
                <FacilitatorProgramAssignment />
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>

        {/* Add/Edit Program Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingProgram ? 'Edit Program' : 'Add New Program'}
            </Modal.Title>
          </Modal.Header>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Programme Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Digital Skills Development"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the program"
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="technical">Technical</option>
                      <option value="business">Business</option>
                      <option value="digital">Digital</option>
                      <option value="entrepreneurship">Entrepreneurship</option>
                      <option value="leadership">Leadership</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Level *</Form.Label>
                    <Form.Select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="expert">Expert</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 8 weeks, 3 months"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Prerequisites</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="prerequisites"
                  value={formData.prerequisites}
                  onChange={handleInputChange}
                  placeholder="Any prerequisites for this program"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Learning Objectives</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="objectives"
                  value={formData.objectives}
                  onChange={handleInputChange}
                  placeholder="Key learning objectives and outcomes"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Capacity</Form.Label>
                <Form.Control
                  type="number"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  min="1"
                  max="1000"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Required Documents for Learners</Form.Label>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={handleSelectAllDocuments}
                      type="button"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeselectAllDocuments}
                      type="button"
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                <Alert variant="info" className="small mb-2">
                  Select which documents learners in this program must upload. Only selected documents will be required for learners enrolled in this program.
                </Alert>
                <Card>
                  <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => (
                      <Form.Check
                        key={key}
                        type="checkbox"
                        id={`doc-${key}`}
                        label={
                          <span>
                            {category.name}
                            {category.mandatory && <Badge bg="warning" className="ms-2">Usually Required</Badge>}
                          </span>
                        }
                        checked={formData.requiredDocuments.includes(key)}
                        onChange={() => handleDocumentToggle(key)}
                        className="mb-2"
                      />
                    ))}
                  </Card.Body>
                </Card>
                <Form.Text className="text-muted">
                  {formData.requiredDocuments.length} document(s) selected
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" />{editingProgram ? 'Updating...' : 'Creating...'}</>
                ) : (
                  editingProgram ? 'Update Programme' : 'Create Programme'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default ProgramManagement;
